import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Box,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Add as AddIcon,
  Upload as UploadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { getDecks, createDeck, deleteDeck } from '../services/decks';
import { exportDeck, importDeck } from '../services/import-export';
import DeckCard from '../components/DeckCard';

function Dashboard() {
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newDeck, setNewDeck] = useState({
    name: '',
    description: '',
    studyMode: 'random'
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldReset, setShouldReset] = useState(false);
  const [imageObjectUrl, setImageObjectUrl] = useState(null);

  useEffect(() => {
    loadDecks();
  }, []);

  useEffect(() => {
    if (shouldReset) {
      setNewDeck({ name: '', description: '', studyMode: 'random' });
      setSelectedImage(null);
      setShouldReset(false);
    }
  }, [openDialog]);

  const loadDecks = async () => {
    try {
      const data = await getDecks();
      setDecks(data);
    } catch (error) {
      console.error('Erreur lors du chargement des paquets:', error);
    }
  };

  const handleAddClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const resetForm = () => {
    setNewDeck({ 
      name: '', 
      description: '', 
      studyMode: 'random' 
    });
    setSelectedImage(null);
    if (imageObjectUrl) {
      URL.revokeObjectURL(imageObjectUrl);
      setImageObjectUrl(null);
    }
  };

  const handleCreateDeck = async () => {
    try {
      if (!newDeck.name.trim()) {
        return;
      }

      setIsLoading(true);
      const formData = new FormData();
      formData.append('name', newDeck.name.trim());
      formData.append('description', newDeck.description.trim());
      formData.append('studyMode', newDeck.studyMode);
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      await createDeck(formData);
      setShouldReset(true);
      setOpenDialog(false);
      await loadDecks();
    } catch (error) {
      console.error('Erreur lors de la création du paquet:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDeck = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce paquet ?')) {
      try {
        await deleteDeck(id);
        loadDecks();
      } catch (error) {
        console.error('Erreur lors de la suppression du paquet:', error);
      }
    }
  };

  const handleExport = async (deckId) => {
    try {
      await exportDeck(deckId);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      // Afficher une notification d'erreur
    }
  };

  const handleImport = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      await importDeck(file);
      loadDecks();
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      // Afficher une notification d'erreur
    }
  };

  const handleEditClick = (deckId) => {
    navigate(`/decks/${deckId}/edit`);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (imageObjectUrl) {
      URL.revokeObjectURL(imageObjectUrl);
    }
    setSelectedImage(file);
    setImageObjectUrl(URL.createObjectURL(file));
  };

  useEffect(() => {
    return () => {
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
      }
    };
  }, [imageObjectUrl]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Mes paquets de cartes
      </Typography>

      <Grid container spacing={3} sx={{ mb: 10 }}>
        {decks.map((deck) => (
          <Grid item xs={12} sm={6} md={4} key={deck.id}>
            <DeckCard
              deck={deck}
              onDelete={handleDeleteDeck}
              onExport={handleExport}
              onEdit={handleEditClick}
            />
          </Grid>
        ))}
      </Grid>

      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAddClick}
      >
        <AddIcon />
      </Fab>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          resetForm();
          setOpenDialog(true);
        }}>
          <ListItemIcon>
            <AddIcon />
          </ListItemIcon>
          <ListItemText primary="Nouveau paquet" />
        </MenuItem>
        <MenuItem>
          <input
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            id="import-deck"
            onChange={(e) => {
              handleImport(e);
              handleMenuClose();
            }}
          />
          <label htmlFor="import-deck" style={{ display: 'flex', alignItems: 'center' }}>
            <ListItemIcon>
              <UploadIcon />
            </ListItemIcon>
            <ListItemText primary="Importer un paquet" />
          </label>
        </MenuItem>
      </Menu>

      <Dialog 
        open={openDialog} 
        onClose={() => {
          setOpenDialog(false);
          resetForm();
        }}
      >
        <DialogTitle>Créer un nouveau paquet</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du paquet"
            fullWidth
            value={newDeck.name}
            onChange={(e) => setNewDeck({ ...newDeck, name: e.target.value })}
            disabled={isLoading}
            error={!newDeck.name.trim() && isLoading}
            helperText={!newDeck.name.trim() && isLoading ? 'Le nom est requis' : ''}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newDeck.description}
            onChange={(e) => setNewDeck({ ...newDeck, description: e.target.value })}
            disabled={isLoading}
          />

          <Box sx={{ mb: 2, mt: 2 }}>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              id="deck-image"
              onChange={handleImageSelect}
              disabled={isLoading}
            />
            <label htmlFor="deck-image">
              <Button
                variant="outlined"
                component="span"
                startIcon={<ImageIcon />}
                fullWidth
                disabled={isLoading}
              >
                {selectedImage ? 'Changer l\'image' : 'Ajouter une image'}
              </Button>
            </label>
            {selectedImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Box
                  component="img"
                  src={imageObjectUrl}
                  alt="Aperçu"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: 1
                  }}
                />
              </Box>
            )}
          </Box>

          <ToggleButtonGroup
            value={newDeck.studyMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setNewDeck({ ...newDeck, studyMode: newMode });
              }
            }}
            fullWidth
          >
            <ToggleButton 
              value="front" 
              sx={{ 
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
              disabled={isLoading}
            >
              Recto
            </ToggleButton>
            <ToggleButton 
              value="back"
              sx={{ 
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
              disabled={isLoading}
            >
              Verso
            </ToggleButton>
            <ToggleButton 
              value="random"
              sx={{ 
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
              disabled={isLoading}
            >
              Aléatoire
            </ToggleButton>
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenDialog(false);
              resetForm();
            }}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button 
              onClick={handleCreateDeck} 
              variant="contained"
              disabled={isLoading || !newDeck.name.trim()}
            >
              {isLoading ? 'Création...' : 'Créer'}
            </Button>
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Dashboard; 